// StandaloneEmitModal — emissão de NFS-e avulsa (sem cobrança prévia).
// Caso de uso: Wesley recebe Pix direto fora do Asaas e precisa de NF.
// Sistema cria charge fantasma "paga_manual" por baixo + emite NF.
// Checkbox "Repetir todo mês" cria um agendamento de NF recorrente
// (POST /api/fiscal/recurring com emit_now=true — emite a 1ª na hora).

import * as Dialog from '@radix-ui/react-dialog';
import { FileText, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { todayLocal } from '@control-center/shared';

import { useEmitStandaloneFiscalInvoice } from '../../hooks/use-fiscal';
import { useCreateRecurringInvoice } from '../../hooks/use-recurring-invoices';
import { extractErrorMessage } from '../../lib/errors';
import type { ClientEntity } from '../../lib/clients-api';
import type { NbsCodeOption } from '../../lib/fiscal-api';
import { ClientPicker } from '../clients/ClientPicker';
import { Field, Input, Textarea } from '../clients/form-primitives';
import { PillButton } from '../clients/PillButton';

import { NbsPicker } from './NbsPicker';

/** Dia default das próximas emissões: hoje, clampado a 28 (limite do backend — meses curtos). */
function defaultBillingDay(): number {
  return Math.min(Number(todayLocal().slice(8, 10)), 28);
}

export interface StandaloneEmitModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function StandaloneEmitModal({
  open,
  onOpenChange,
  onSuccess,
}: StandaloneEmitModalProps): JSX.Element {
  const [client, setClient] = useState<ClientEntity | null>(null);
  const [amount, setAmount] = useState('');
  const [serviceDescription, setServiceDescription] = useState('');
  const [emissionDate, setEmissionDate] = useState<string>(todayLocal());
  const [repeatMonthly, setRepeatMonthly] = useState(false);
  const [billingDay, setBillingDay] = useState<string>(String(defaultBillingDay()));
  const [customService, setCustomService] = useState(false);
  const [municipalServiceCode, setMunicipalServiceCode] = useState('');
  const [municipalServiceName, setMunicipalServiceName] = useState('');
  const [issRate, setIssRate] = useState('');
  const [nbs, setNbs] = useState<NbsCodeOption | null>(null);

  const mutation = useEmitStandaloneFiscalInvoice();
  const recurringMutation = useCreateRecurringInvoice();

  function reset(): void {
    setClient(null);
    setAmount('');
    setServiceDescription('');
    setEmissionDate(todayLocal());
    setRepeatMonthly(false);
    setBillingDay(String(defaultBillingDay()));
    setCustomService(false);
    setMunicipalServiceCode('');
    setMunicipalServiceName('');
    setIssRate('');
    setNbs(null);
  }

  function handleOpenChange(next: boolean): void {
    if (!next) reset();
    onOpenChange(next);
  }

  const amountNum = Number.parseFloat(amount);
  const amountValid = Number.isFinite(amountNum) && amountNum > 0;
  const descValid = serviceDescription.trim().length >= 10;
  const billingDayNum = Number.parseInt(billingDay, 10);
  const billingDayValid =
    Number.isInteger(billingDayNum) && billingDayNum >= 1 && billingDayNum <= 28;
  // Trio fiscal (natureza própria): os 3 juntos ou nenhum. Espelha o refine do backend.
  const serviceCodeValid =
    municipalServiceCode.trim().length >= 1 && municipalServiceCode.trim().length <= 20;
  const serviceNameValid = municipalServiceName.trim().length >= 3;
  const issRateNum = Number.parseFloat(issRate);
  const issRateValid = Number.isFinite(issRateNum) && issRateNum >= 0 && issRateNum <= 100;
  const customServiceValid =
    !customService || (serviceCodeValid && serviceNameValid && issRateValid);
  const isValid =
    client !== null &&
    amountValid &&
    descValid &&
    (!repeatMonthly || billingDayValid) &&
    customServiceValid;
  const isPending = mutation.isPending || recurringMutation.isPending;

  async function handleSubmit(): Promise<void> {
    if (!client || !isValid) return;
    // Natureza fiscal própria — só vai quando o operador abre a seção avançada.
    const serviceTaxonomy = customService
      ? {
          municipal_service_code: municipalServiceCode.trim(),
          municipal_service_name: municipalServiceName.trim(),
          iss_rate: issRateNum,
        }
      : {};
    // NBS só vai quando escolhido — sem ele a NF sai igual à de hoje.
    const nbsPayload = nbs ? { nbs_code: nbs.code } : {};
    try {
      if (repeatMonthly) {
        // Recorrente: cria agendamento + emite a 1ª NF na hora (emit_now).
        const recurring = await recurringMutation.mutateAsync({
          client_id: client.id,
          amount,
          service_description: serviceDescription.trim(),
          billing_day: billingDayNum,
          emit_now: true,
          ...serviceTaxonomy,
          ...nbsPayload,
        });
        if (recurring.lastError) {
          toast.warning('Recorrência criada, mas a primeira emissão falhou.', {
            description: `${recurring.lastError} — acompanhe em Faturamento → NF recorrente.`,
          });
        } else {
          toast.success('NF emitida + recorrência mensal criada.', {
            description: `Próximas saem todo dia ${billingDayNum} — gerencie em Faturamento → NF recorrente.`,
          });
        }
      } else {
        const result = await mutation.mutateAsync({
          client_id: client.id,
          amount,
          service_description: serviceDescription.trim(),
          emission_date: emissionDate,
          ...serviceTaxonomy,
          ...nbsPayload,
        });
        if (result.status === 'issued') {
          toast.success('NF avulsa emitida.', {
            description: result.externalInvoiceId
              ? `Nº externo: ${result.externalInvoiceId}`
              : undefined,
          });
        } else {
          toast.success('NF criada no Asaas — aguardando autorização da prefeitura.', {
            description: 'Acompanhe o status em Faturamento → Notas fiscais.',
          });
        }
      }
      onSuccess?.();
      handleOpenChange(false);
    } catch (err) {
      toast.error('Não conseguimos emitir a NF.', {
        description: extractErrorMessage(err, 'Tente novamente.'),
      });
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px]" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[min(560px,calc(100vw-32px))] max-h-[calc(100vh-48px)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-[14px] border border-line bg-panel p-6 shadow-lg focus:outline-none">
          <div className="mb-5 flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-brand-soft text-brand">
                <FileText size={15} strokeWidth={1.8} />
              </span>
              <div>
                <Dialog.Title className="text-[16px] font-semibold tracking-[-0.005em] text-ink">
                  Emitir NF avulsa
                </Dialog.Title>
                <Dialog.Description className="mt-[2px] text-[12px] text-muted">
                  Pra serviços recebidos fora do Asaas (Pix direto, dinheiro, etc).
                </Dialog.Description>
              </div>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Fechar"
                className="flex h-8 w-8 items-center justify-center rounded-[6px] text-muted hover:bg-panel-2 hover:text-ink"
              >
                <X size={15} strokeWidth={2} />
              </button>
            </Dialog.Close>
          </div>

          <div className="flex flex-col gap-4">
            <Field label="Cliente" required>
              {() => <ClientPicker value={client} onChange={setClient} />}
            </Field>

            <div className="grid grid-cols-2 gap-4">
              <Field
                label="Valor (R$)"
                required
                hint="Use ponto como separador decimal — ex: 1500.00"
              >
                {({ inputId }) => (
                  <Input
                    id={inputId}
                    tabular
                    inputMode="decimal"
                    placeholder="0.00"
                    hasError={amount !== '' && !amountValid}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                )}
              </Field>

              <Field
                label="Data da NF"
                hint={
                  repeatMonthly
                    ? 'Recorrente sempre emite na data do dia.'
                    : 'Hoje por default. Até 30 dias atrás.'
                }
              >
                {({ inputId }) => (
                  <Input
                    id={inputId}
                    type="date"
                    value={repeatMonthly ? todayLocal() : emissionDate}
                    max={todayLocal()}
                    disabled={repeatMonthly}
                    onChange={(e) => setEmissionDate(e.target.value)}
                  />
                )}
              </Field>
            </div>

            <Field
              label="Descrição do serviço"
              required
              hint="Vira a Discriminação na NF. Mínimo 10 caracteres."
              error={serviceDescription !== '' && !descValid ? 'Mínimo 10 caracteres.' : undefined}
            >
              {({ inputId }) => (
                <Textarea
                  id={inputId}
                  rows={3}
                  placeholder="Ex: Implantação inicial do sistema Reservei.app — instalação + treinamento"
                  hasError={serviceDescription !== '' && !descValid}
                  value={serviceDescription}
                  onChange={(e) => setServiceDescription(e.target.value)}
                />
              )}
            </Field>

            <Field
              label="Código NBS"
              hint={
                repeatMonthly
                  ? 'Opcional. Fica guardado e vai em todas as próximas NFs.'
                  : 'Opcional. Nomenclatura Brasileira de Serviços — escolha da lista oficial.'
              }
            >
              {({ inputId, describedBy }) => (
                <NbsPicker id={inputId} describedBy={describedBy} value={nbs} onChange={setNbs} />
              )}
            </Field>

            <div className="flex flex-col gap-3 rounded-[10px] border border-line/60 bg-panel-2 p-3">
              <label className="flex items-start gap-2 text-[13px] text-ink-2">
                <input
                  type="checkbox"
                  className="mt-[2px] h-4 w-4 rounded border-line bg-panel-2 text-accent focus:ring-0"
                  checked={customService}
                  onChange={(e) => setCustomService(e.target.checked)}
                />
                <span>
                  Outro tipo de serviço (não é software)
                  <span className="mt-[2px] block text-[11px] font-normal text-muted-2">
                    Por padrão a NF sai como licenciamento de software. Marque se este serviço tem
                    natureza fiscal própria — código, nome e ISS vêm do seu contador.
                  </span>
                </span>
              </label>

              {customService ? (
                <div className="flex flex-col gap-3 border-t border-line/60 pt-3">
                  <div className="grid grid-cols-[1fr_6rem] gap-3">
                    <Field
                      label="Código de serviço"
                      required
                      hint="Ex: 17.01.02"
                      error={
                        municipalServiceCode !== '' && !serviceCodeValid
                          ? 'Até 20 caracteres.'
                          : undefined
                      }
                    >
                      {({ inputId }) => (
                        <Input
                          id={inputId}
                          placeholder="17.01.02"
                          hasError={municipalServiceCode !== '' && !serviceCodeValid}
                          value={municipalServiceCode}
                          onChange={(e) => setMunicipalServiceCode(e.target.value)}
                        />
                      )}
                    </Field>

                    <Field
                      label="ISS (%)"
                      required
                      hint="Ex: 3"
                      error={issRate !== '' && !issRateValid ? '0 a 100.' : undefined}
                    >
                      {({ inputId }) => (
                        <Input
                          id={inputId}
                          tabular
                          inputMode="decimal"
                          placeholder="0"
                          hasError={issRate !== '' && !issRateValid}
                          value={issRate}
                          onChange={(e) => setIssRate(e.target.value)}
                        />
                      )}
                    </Field>
                  </div>

                  <Field
                    label="Nome do serviço"
                    required
                    hint="O texto que descreve a natureza fiscal — vem do contador."
                    error={
                      municipalServiceName !== '' && !serviceNameValid
                        ? 'Mínimo 3 caracteres.'
                        : undefined
                    }
                  >
                    {({ inputId }) => (
                      <Textarea
                        id={inputId}
                        rows={2}
                        placeholder="Ex: Análise, coleta, compilação e fornecimento de dados e informações de qualquer natureza"
                        hasError={municipalServiceName !== '' && !serviceNameValid}
                        value={municipalServiceName}
                        onChange={(e) => setMunicipalServiceName(e.target.value)}
                      />
                    )}
                  </Field>
                </div>
              ) : null}
            </div>

            <div className="flex flex-col gap-3 rounded-[10px] border border-line/60 bg-panel-2 p-3">
              <label className="flex items-center gap-2 text-[13px] text-ink-2">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-line bg-panel-2 text-accent focus:ring-0"
                  checked={repeatMonthly}
                  onChange={(e) => setRepeatMonthly(e.target.checked)}
                />
                Repetir todo mês (emite uma NF igual automaticamente)
              </label>

              {repeatMonthly ? (
                <Field
                  label="Dia do mês das próximas emissões"
                  required
                  hint="De 1 a 28 — evita pular fevereiro e meses curtos."
                  error={
                    billingDay !== '' && !billingDayValid ? 'Use um dia entre 1 e 28.' : undefined
                  }
                >
                  {({ inputId }) => (
                    <Input
                      id={inputId}
                      tabular
                      inputMode="numeric"
                      className="w-24"
                      hasError={billingDay !== '' && !billingDayValid}
                      value={billingDay}
                      onChange={(e) => setBillingDay(e.target.value)}
                    />
                  )}
                </Field>
              ) : null}
            </div>

            <div className="rounded-[10px] border border-line/60 bg-panel-2 p-3 text-[11px] text-muted-2">
              Por baixo, o sistema cria uma cobrança marcada como{' '}
              <strong className="text-muted">paga manualmente</strong> pra manter o registro
              contábil — você não precisa fazer nada além de preencher acima.
            </div>
          </div>

          <div className="mt-6 flex items-center justify-end gap-2">
            <PillButton onClick={() => handleOpenChange(false)} disabled={isPending}>
              Cancelar
            </PillButton>
            <PillButton solid onClick={() => void handleSubmit()} disabled={!isValid || isPending}>
              {isPending ? 'Emitindo…' : repeatMonthly ? 'Emitir e repetir todo mês' : 'Emitir NF'}
            </PillButton>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
